import { Box, GlobalStyles, Typography } from "@mui/material";
import { toSvg } from "html-to-image";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ExportThemeProvider, darkTheme, lightTheme } from "../../theme";
import { downloadBlob } from "./download";
import { BRAND, BrandHeader, PRODUCT, dateLine } from "./ExportFrame";
import { pngOfSvg } from "./raster";
import type { ExportMeta } from "./table";

export interface CaptureOptions {
  mode: "light" | "dark";
  format: "png" | "svg";
  // Width (px) the subtree is laid out at; its height follows its content
  width?: number;
  filename: string;
}

export interface PrintOptions {
  // The content opens with a title of its own (a product's name), so the
  // header does not repeat the title of the meta
  titled?: boolean;
}

export interface ExportStage {
  // Renders `node` off screen in the theme asked for and saves it as an image
  capture: (node: ReactNode, options: CaptureOptions) => Promise<void>;
  // Lays `node` out under a header in the light theme and opens the print
  // dialog, from which the page is saved as a PDF or printed
  print: (
    node: ReactNode,
    meta: ExportMeta,
    options?: PrintOptions,
  ) => Promise<void>;
}

const NOT_MOUNTED = "Export is not available here";

const UNMOUNTED: ExportStage = {
  capture: () => Promise.reject(new Error(NOT_MOUNTED)),
  print: () => Promise.reject(new Error(NOT_MOUNTED)),
};

const ExportStageContext = createContext<ExportStage>(UNMOUNTED);

// Called by a subtree being captured to say it has drawn what it will draw;
// a no-op anywhere else
const ExportReadyContext = createContext<() => void>(() => {});

/**
 * For a pane whose content arrives late (a fetch, a layout pass): call the
 * returned function once it is drawn, and the capture goes ahead without
 * waiting out the settle heuristic. Panes that never call it are captured
 * once their markup has stopped changing.
 */
export const useExportReady = (): (() => void) => useContext(ExportReadyContext);

// Whether the subtree is drawn for a printed page: true only inside the
// print layer, so a control of the screen (a source chip) can leave itself out
const PrintingContext = createContext(false);

export const usePrinting = (): boolean => useContext(PrintingContext);

// Whether the subtree is drawn for an exported image: true only inside the
// capture, so a view can leave out what only operates it (its controls, its
// panel) and keep what reads it (the chart, its legend)
const CapturingContext = createContext(false);

export const useCapturing = (): boolean => useContext(CapturingContext);

export const useExportStage = (): ExportStage => useContext(ExportStageContext);

// Width (px) a capture is laid out at when none is asked for
const DEFAULT_WIDTH = 1600;
// Device pixels per CSS pixel in a PNG
const PIXEL_RATIO = 2;
// Frames the markup must keep still before a capture counts as settled
const STABLE_FRAMES = 10;
// Longest (ms) a capture waits for the subtree to settle or to say it is ready
const READY_TIMEOUT_MS = 1500;
// Longest (ms) after window.print() returns before the layer is removed
// where the browser never fires afterprint
const AFTERPRINT_FALLBACK_MS = 2000;

const PRINT_LAYER_CLASS = "export-print-layer";
const PRINT_CONTENT_CLASS = "export-print-content";
const PRINTING_CLASS = "export-printing";

const nextFrame = (): Promise<void> =>
  new Promise((resolve) => window.requestAnimationFrame(() => resolve()));

// What changes while a subtree is still drawing: its markup, and where React
// Flow has fitted its viewport
const signatureOf = (element: HTMLElement): string =>
  [
    element.innerHTML.length,
    element.querySelectorAll("canvas").length,
    ...Array.from(
      element.querySelectorAll<HTMLElement>(".react-flow__viewport"),
      (viewport) => viewport.style.transform,
    ),
  ].join("|");

// Waits until the subtree is drawn. Two frames let React commit and the
// browser lay out; then it is ready when it says so (useExportReady), or
// when its signature has held for STABLE_FRAMES frames with no progress
// indicator showing. React Flow fits its viewport a frame or two after its
// nodes are measured, and that move is part of the signature, so a map is
// never caught mid-fit. The timeout keeps a pane that never settles from
// holding the export.
const settle = async (
  element: HTMLElement,
  signalled: () => boolean,
  timeoutMs: number,
): Promise<void> => {
  await nextFrame();
  await nextFrame();
  const deadline = Date.now() + timeoutMs;
  let last = signatureOf(element);
  let still = 0;
  while (Date.now() < deadline && !signalled() && still < STABLE_FRAMES) {
    await nextFrame();
    const now = signatureOf(element);
    const loading = element.querySelector('[role="progressbar"]') !== null;
    still = now === last && !loading ? still + 1 : 0;
    last = now;
  }
  // Fonts that arrive late change the layout of text
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
};

// The control characters XML does not allow (all below U+0020 but tab, line
// feed and carriage return), as toSvg's data URL encodes them
const XML_FORBIDDEN = /%(?:0[0-8BCEF]|1[0-9A-F])/gi;

/**
 * A data URL made by toSvg, without the characters XML does not allow. The
 * markup copies text and attributes as they are, and a single such character
 * (in an id, or in a name from the data) leaves an SVG no reader opens and
 * no browser draws.
 */
export const wellFormedSvg = (dataUrl: string): string =>
  dataUrl.replace(XML_FORBIDDEN, "");

// What an SVG shape may take from a style sheet (a class of sx) rather than
// from its attributes or its own style
const SVG_STYLE_PROPERTIES = [
  "fill",
  "fill-opacity",
  "fill-rule",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-linecap",
  "stroke-linejoin",
  "opacity",
  "visibility",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "letter-spacing",
  "text-anchor",
  "dominant-baseline",
] as const;

/**
 * Writes into the own style of every element inside an <svg> under `root`
 * how it is painted. toSvg copies an <svg> as it is, without the computed
 * styles it writes onto every other element, so a shape painted by a class
 * would otherwise lose its colours (and be drawn black, SVG's default).
 */
export const inlineSvgStyles = (root: HTMLElement): void => {
  root.querySelectorAll<SVGElement>("svg *").forEach((element) => {
    const computed = window.getComputedStyle(element);
    for (const property of SVG_STYLE_PROPERTIES) {
      const value = computed.getPropertyValue(property);
      if (value) {
        element.style.setProperty(property, value);
      }
    }
  });
};

// The text of a data URL made by toSvg, as a file
const svgBlob = (dataUrl: string): Blob =>
  new Blob([decodeURIComponent(dataUrl.slice(dataUrl.indexOf(",") + 1))], {
    type: "image/svg+xml",
  });

interface CaptureJob {
  node: ReactNode;
  options: CaptureOptions;
  done: () => void;
  fail: (exc: unknown) => void;
}

// The off-screen copy of a subtree, drawn and saved once, then reported done
const CaptureHost: React.FC<{ job: CaptureJob }> = ({ job }) => {
  const { node, options, done, fail } = job;
  const target = useRef<HTMLDivElement>(null);
  const ready = useRef(false);
  const signal = useCallback(() => {
    ready.current = true;
  }, []);
  const theme = options.mode === "light" ? lightTheme : darkTheme;
  const background = theme.palette.background.default;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const element = target.current;
      if (!element) {
        throw new Error("The export could not be drawn");
      }
      await settle(element, () => ready.current, READY_TIMEOUT_MS);
      if (cancelled) {
        return;
      }
      // The copy is the export's own and goes once saved, so its markup may
      // be written to
      inlineSvgStyles(element);
      // A PNG is drawn from the SVG (pngOfSvg), not by toBlob, which hangs
      // where the browser cannot decode the image
      const svg = wellFormedSvg(
        await toSvg(element, { backgroundColor: background }),
      );
      downloadBlob(
        options.filename,
        options.format === "png"
          ? await pngOfSvg(svg, background, PIXEL_RATIO)
          : svgBlob(svg),
      );
    };
    run().then(done, fail);
    return () => {
      cancelled = true;
    };
    // The job is fixed for the life of the host
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <ExportThemeProvider mode={options.mode}>
      <ExportReadyContext.Provider value={signal}>
        {/* Off screen, not hidden: a hidden subtree has no layout to draw */}
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            left: -100000,
            top: 0,
            width: options.width ?? DEFAULT_WIDTH,
            pointerEvents: "none",
          }}
        >
          <Box
            ref={target}
            data-testid="export-capture"
            sx={{
              width: options.width ?? DEFAULT_WIDTH,
              display: "flex",
              flexDirection: "column",
              backgroundColor: background,
              color: "text.primary",
            }}
          >
            <CapturingContext.Provider value>{node}</CapturingContext.Provider>
          </Box>
        </Box>
      </ExportReadyContext.Provider>
    </ExportThemeProvider>,
    document.body,
  );
};

interface PrintJob {
  node: ReactNode;
  meta: ExportMeta;
  options: PrintOptions;
  // Reports the layer drawn, ready for the print dialog
  drawn: () => void;
  fail: (exc: unknown) => void;
}

// The printed page: A4 with no margin of its own. The browser prints its
// own header and footer (the title, the address, the page number) in the
// page's margin, and leaves them out where there is none; the margins are
// drawn by the layer instead (PAGE_MARGIN).
const PAGE_RULES = "@page { size: A4; margin: 0; }";

// The margins of every printed page. The sides are padding of the layer.
// The top and the bottom are the head and the foot of a table around the
// layer, which the browser repeats on every page it breaks the table over.
const PAGE_MARGIN = { side: "14mm", top: "14mm", bottom: "16mm" } as const;

// The page that is printed: the brand and the date, the title and the
// filters where the content does not carry them, then the content, and at
// the foot of every page what it is from
const PrintLayer: React.FC<{ job: PrintJob }> = ({ job }) => {
  const { node, meta, options, drawn, fail } = job;
  const titled = options.titled ?? false;
  const layer = useRef<HTMLDivElement>(null);
  const ready = useRef(false);
  const signal = useCallback(() => {
    ready.current = true;
  }, []);

  useEffect(() => {
    const element = layer.current;
    if (!element) {
      fail(new Error("The page could not be drawn"));
      return;
    }
    // The layer is not displayed on screen, so there is no layout to wait
    // for: two frames let React commit, then the dialog may open
    settle(element, () => ready.current, 0).then(drawn, fail);
    // The job is fixed for the life of the layer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const footer = [meta.source ?? `${BRAND} ${PRODUCT}`, dateLine(meta.generatedAt)]
    .join(" · ");

  return createPortal(
    <ExportThemeProvider mode="light">
      <ExportReadyContext.Provider value={signal}>
        <Box
          ref={layer}
          className={PRINT_LAYER_CLASS}
          data-testid="export-print-layer"
          sx={{
            px: PAGE_MARGIN.side,
            backgroundColor: "background.default",
            color: "text.primary",
          }}
        >
          <style>{PAGE_RULES}</style>
          <Box
            component="table"
            role="presentation"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              "& > * > tr > td": { p: 0 },
            }}
          >
            <thead>
              <tr>
                <td>
                  <Box sx={{ height: PAGE_MARGIN.top }} />
                </td>
              </tr>
            </thead>
            <tfoot>
              <tr>
                <td>
                  <Box
                    sx={{
                      height: PAGE_MARGIN.bottom,
                      pt: "5mm",
                      boxSizing: "border-box",
                      fontSize: "7.5pt",
                      color: "text.secondary",
                    }}
                  >
                    {footer}
                  </Box>
                </td>
              </tr>
            </tfoot>
            <tbody>
              <tr>
                <td className={PRINT_CONTENT_CLASS}>
                  <BrandHeader date={meta.generatedAt} />
                  {(!titled || meta.filters?.length || meta.note) && (
                    <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {!titled && (
                        <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
                          {meta.title}
                        </Typography>
                      )}
                      {meta.filters?.map(([label, value]) => (
                        <Typography key={label} variant="body2" color="text.secondary">
                          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                            {label}:
                          </Box>{" "}
                          {value}
                        </Typography>
                      ))}
                      {meta.note && (
                        <Typography variant="body2" color="text.secondary">
                          {meta.note}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Box sx={{ mt: 3 }}>
                    <PrintingContext.Provider value>{node}</PrintingContext.Provider>
                  </Box>
                </td>
              </tr>
            </tbody>
          </Box>
        </Box>
      </ExportReadyContext.Provider>
    </ExportThemeProvider>,
    document.body,
  );
};

// The rules of the print layer. On screen it is not displayed. While it is
// printed, the body shows nothing but the layer (the app, the menus and the
// dialogs are all children of the body), with its colours kept; a row of a
// table of the content is not split across pages, nor a heading parted from
// what follows. The page itself is set by the layer (PAGE_RULES).
const PRINT_STYLES = {
  [`.${PRINT_LAYER_CLASS}`]: { display: "none" },
  "@media print": {
    [`.${PRINT_CONTENT_CLASS} tr`]: { breakInside: "avoid" },
    [`.${PRINT_CONTENT_CLASS} :is(h1, h2, h3)`]: { breakAfter: "avoid" },
    [`html.${PRINTING_CLASS}, body.${PRINTING_CLASS}`]: {
      height: "auto !important",
      overflow: "visible !important",
      backgroundColor: "#fff !important",
    },
    [`body.${PRINTING_CLASS} > *:not(.${PRINT_LAYER_CLASS})`]: {
      display: "none !important",
    },
    [`body.${PRINTING_CLASS} > .${PRINT_LAYER_CLASS}`]: {
      display: "block !important",
      printColorAdjust: "exact",
      WebkitPrintColorAdjust: "exact",
    },
  },
};

export const ExportStageProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [capturing, setCapturing] = useState<CaptureJob | null>(null);
  const [printing, setPrinting] = useState<PrintJob | null>(null);
  const busy = useRef(false);

  const capture = useCallback(
    (node: ReactNode, options: CaptureOptions): Promise<void> => {
      if (busy.current) {
        return Promise.reject(new Error("Another export is still running"));
      }
      busy.current = true;
      return new Promise<void>((resolve, reject) => {
        const finish = (outcome: () => void) => {
          busy.current = false;
          setCapturing(null);
          outcome();
        };
        setCapturing({
          node,
          options,
          done: () => finish(resolve),
          fail: (exc) => finish(() => reject(exc)),
        });
      });
    },
    [],
  );

  const print = useCallback(
    (node: ReactNode, meta: ExportMeta, options: PrintOptions = {}): Promise<void> => {
      if (busy.current) {
        return Promise.reject(new Error("Another export is still running"));
      }
      busy.current = true;
      // The browser names the saved PDF after the document's title
      const appTitle = document.title;
      return new Promise<void>((resolve, reject) => {
        let cleaned = false;
        const clean = () => {
          if (cleaned) {
            return;
          }
          cleaned = true;
          window.removeEventListener("afterprint", clean);
          document.documentElement.classList.remove(PRINTING_CLASS);
          document.body.classList.remove(PRINTING_CLASS);
          document.title = appTitle;
          busy.current = false;
          setPrinting(null);
          resolve();
        };
        const fail = (exc: unknown) => {
          window.removeEventListener("afterprint", clean);
          document.documentElement.classList.remove(PRINTING_CLASS);
          document.body.classList.remove(PRINTING_CLASS);
          document.title = appTitle;
          busy.current = false;
          setPrinting(null);
          reject(exc);
        };
        const drawn = () => {
          window.addEventListener("afterprint", clean);
          document.documentElement.classList.add(PRINTING_CLASS);
          document.body.classList.add(PRINTING_CLASS);
          document.title = `${meta.title} – ${BRAND}`;
          // print() blocks in most browsers and afterprint follows; where
          // it does not fire, the layer goes after a pause
          window.print();
          window.setTimeout(clean, AFTERPRINT_FALLBACK_MS);
        };
        setPrinting({ node, meta, options, drawn, fail });
      });
    },
    [],
  );

  const stage = useMemo(() => ({ capture, print }), [capture, print]);

  return (
    <ExportStageContext.Provider value={stage}>
      <GlobalStyles styles={PRINT_STYLES} />
      {children}
      {capturing && <CaptureHost job={capturing} />}
      {printing && <PrintLayer job={printing} />}
    </ExportStageContext.Provider>
  );
};
